import {
  ArrowLeftIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@praetorian-chariot/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';

import { CapabilitySchedule } from '@/hooks/useCapabilitySchedule';
import { cn } from '@/utils/classname';

import { type WeeklyScheduleState } from './WeeklySchedule';

export interface ExistingSchedulesPanelProps {
  capabilityName: string;
  capabilityKey: string;
  schedules: CapabilitySchedule[];
  isOpen: boolean;
  onClose: () => void;
  onCreateNew: () => void;
  onEdit: (schedule: CapabilitySchedule) => void;
  onPause: (schedule: CapabilitySchedule) => void;
  onResume: (schedule: CapabilitySchedule) => void;
  onDelete: (schedule: CapabilitySchedule) => void;
}

/**
 * Format the enabled days from a WeeklyScheduleState into a readable string
 * e.g., "Mon, Wed, Fri" or "Daily"
 */
function formatScheduleDays(schedule: WeeklyScheduleState): string {
  const dayMap: Record<keyof WeeklyScheduleState, string> = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };

  const enabledDays = (
    Object.entries(schedule) as [
      keyof WeeklyScheduleState,
      { enabled: boolean; time: string },
    ][]
  )
    .filter(([_, daySchedule]) => daySchedule.enabled)
    .map(([day]) => dayMap[day]);

  if (enabledDays.length === 7) {
    return 'Daily';
  }
  if (
    enabledDays.length === 5 &&
    !schedule.saturday.enabled &&
    !schedule.sunday.enabled
  ) {
    return 'Weekdays';
  }
  if (
    enabledDays.length === 2 &&
    schedule.saturday.enabled &&
    schedule.sunday.enabled
  ) {
    return 'Weekends';
  }
  return enabledDays.join(', ');
}

/**
 * Get the time from the first enabled day in the schedule
 */
function getScheduleTime(schedule: WeeklyScheduleState): string {
  const enabledDay = Object.values(schedule).find(d => d.enabled);
  return enabledDay?.time || '00:00';
}

/**
 * Extract target name from target key (removes #asset# prefix)
 */
function getTargetDisplayName(targetKey: string): string {
  return targetKey.replace(/^#asset#/, '');
}

/**
 * ExistingSchedulesPanel - Slide-over panel for managing existing schedules
 *
 * Displays a list of schedule cards for a capability with actions to:
 * - Edit schedule configuration
 * - Pause/Resume schedules
 * - Delete schedules
 * - Create new schedules
 *
 * Designed to slide in from the right within the drawer container.
 */
export function ExistingSchedulesPanel({
  capabilityName,
  schedules,
  isOpen,
  onClose,
  onCreateNew,
  onEdit,
  onPause,
  onResume,
  onDelete,
}: ExistingSchedulesPanelProps) {
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  // Focus management: Focus back button when panel opens
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element for returning focus on close
      previousActiveElement.current = document.activeElement;
      // Focus the back button when panel opens
      setTimeout(() => {
        backButtonRef.current?.focus();
      }, 100); // Small delay to allow animation to start
    } else if (previousActiveElement.current instanceof HTMLElement) {
      // Return focus when panel closes
      previousActiveElement.current.focus();
    }
  }, [isOpen]);

  // Handle Escape key to close panel
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={`Existing schedules for ${capabilityName}`}
          data-testid="existing-schedules-panel"
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="bg-chariot-background-primary absolute inset-0 z-10 flex flex-col"
        >
          {/* Header */}
          <div className="border-chariot-stroke flex items-center gap-3 border-b p-4 sm:p-6">
            <button
              ref={backButtonRef}
              onClick={onClose}
              className="text-chariot-text-secondary hover:text-chariot-text-primary flex items-center gap-2 text-sm transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
              aria-label={`Back to ${capabilityName}`}
            >
              <ArrowLeftIcon className="size-4" aria-hidden="true" />
              <span className="hidden sm:inline">Back to {capabilityName}</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>

          {/* Title */}
          <div className="px-4 pt-4 sm:px-6 sm:pt-6">
            <h2 className="text-chariot-text-primary text-lg font-semibold">
              Existing Schedules
            </h2>
            <p className="text-chariot-text-secondary mt-1 text-sm">
              {schedules.length} schedule{schedules.length !== 1 ? 's' : ''} for
              this capability
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-4 sm:p-6">
            {schedules.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-4">
                {schedules.map(schedule => (
                  <ScheduleCard
                    key={schedule.scheduleId}
                    schedule={schedule}
                    onEdit={() => onEdit(schedule)}
                    onPause={() => onPause(schedule)}
                    onResume={() => onResume(schedule)}
                    onDelete={() => onDelete(schedule)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-chariot-stroke flex justify-end border-t p-4 sm:p-6">
            <Button
              size="sm"
              styleType="primary"
              startIcon={<PlusIcon className="size-4" />}
              label="Create New Schedule"
              onClick={onCreateNew}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Empty state shown when no schedules exist
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="bg-chariot-background-secondary mb-4 rounded-full p-4">
        <PauseIcon className="text-chariot-text-tertiary size-8" />
      </div>
      <p className="text-chariot-text-secondary mb-2 text-sm font-medium">
        No schedules found
      </p>
      <p className="text-chariot-text-tertiary text-sm">
        Create a new schedule to get started
      </p>
    </div>
  );
}

/**
 * Individual schedule card with actions
 */
interface ScheduleCardProps {
  schedule: CapabilitySchedule;
  onEdit: () => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => void;
}

function ScheduleCard({
  schedule,
  onEdit,
  onPause,
  onResume,
  onDelete,
}: ScheduleCardProps) {
  const targetName = getTargetDisplayName(schedule.targetKey);
  const days = formatScheduleDays(schedule.weeklySchedule);
  const time = getScheduleTime(schedule.weeklySchedule);
  const isActive = schedule.status === 'active';
  const isPaused = schedule.status === 'paused';

  return (
    <div className="border-chariot-stroke bg-layer1 rounded-lg border p-4">
      {/* Header row: Target name and status */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex-1 overflow-hidden">
          <p className="text-chariot-text-primary truncate font-medium">
            {targetName}
          </p>
        </div>
        <StatusBadge status={schedule.status} />
      </div>

      {/* Schedule details */}
      <div className="text-chariot-text-secondary mb-4 text-sm">
        <span>{days}</span>
        <span className="mx-2">at</span>
        <span>{time} UTC</span>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          styleType="secondary"
          startIcon={<PencilIcon className="size-3" />}
          label="Edit"
          onClick={onEdit}
        />
        {isActive && (
          <Button
            size="sm"
            styleType="secondary"
            startIcon={<PauseIcon className="size-3" />}
            label="Pause"
            onClick={onPause}
          />
        )}
        {isPaused && (
          <Button
            size="sm"
            styleType="secondary"
            startIcon={<PlayIcon className="size-3" />}
            label="Resume"
            onClick={onResume}
          />
        )}
        <Button
          size="sm"
          styleType="secondary"
          startIcon={<TrashIcon className="size-3" />}
          label="Delete"
          onClick={onDelete}
        />
      </div>
    </div>
  );
}

/**
 * Status badge component for schedule status
 */
function StatusBadge({ status }: { status: string }) {
  const isActive = status === 'active';
  const isPaused = status === 'paused';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        isActive &&
          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        isPaused &&
          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        !isActive &&
          !isPaused &&
          'bg-chariot-background-secondary text-chariot-text-secondary'
      )}
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          isActive && 'bg-green-500',
          isPaused && 'bg-yellow-500',
          !isActive && !isPaused && 'bg-chariot-text-tertiary'
        )}
      />
      <span>{isActive ? 'Active' : isPaused ? 'Paused' : status}</span>
    </span>
  );
}
